import React, { useEffect, useState } from "react";
import { Button } from "primereact/button";
import localeText from "../../../../../../../locale";
import FormSectionHeader from "../components/FormSectionHeader";
import FileDropzone from "../components/FileDropzone";
import { IMAGE_MAX_BYTES, MAX_IMAGES } from "../constants";
import { formatFileSize } from "../utils";

function FileListItem({ file, isPrimary, onRemove, isImage }) {
  const [preview, setPreview] = useState(file?.previewUrl || null);

  useEffect(() => {
    if (!isImage || !file) return undefined;
    if (file.previewUrl) {
      setPreview(file.previewUrl);
      return undefined;
    }
    if (file instanceof Blob) {
      const url = URL.createObjectURL(file);
      setPreview(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreview(null);
    return undefined;
  }, [file, isImage]);

  return (
    <li className="product-form-file-item">
      <div className="product-form-file-thumb" style={{ width: "4rem", height: "4rem" }}>
        {isImage && preview ? <img src={preview} alt="" /> : <i className="pi pi-image" />}
      </div>
      <div className="product-form-file-info">
        <div className="product-form-file-name flex align-items-center gap-2">
          {file?.name || file?.fileName || "—"}
          {isPrimary && (
            <span className="product-form-primary-tag"><i className="pi pi-star-fill text-xs" /> {localeText("primaryImage")}</span>
          )}
        </div>
        <div className="product-form-help">{formatFileSize(file?.size ?? file?.fileSize)}</div>
      </div>

      <Button
        type="button"
        icon="pi pi-trash"
        className="p-button-text p-button-rounded p-no-outline"
        style={{ color: "var(--theme-text-muted)" }}
        onClick={onRemove}
      />
    </li>
  );
}

function ImagesSection({ form }) {
  const isService = form.productType === 2;
  const svgIcon = (
    <svg width="24" height="21" viewBox="0 0 24 21" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M19.95 12.6V15.75H23.1V17.85H19.95V21H17.85V17.85H14.7V15.75H17.85V12.6H19.95ZM19.9584 0C20.5338 0 21 0.46725 21 1.04265V10.8591C20.3255 10.6209 19.6153 10.4994 18.9 10.5V2.1H2.1L2.10105 16.8L11.8576 7.04235C12.0382 6.8612 12.2787 6.75227 12.534 6.73603C12.7892 6.71978 13.0416 6.79735 13.2436 6.95415L13.3413 7.0434L17.0646 10.7709C16.2505 11.019 15.495 11.4294 14.8438 11.9773C14.1926 12.5253 13.659 13.1994 13.2754 13.9591C12.8918 14.7188 12.666 15.5484 12.6116 16.3977C12.5572 17.2471 12.6755 18.0986 12.9591 18.901L1.0416 18.9C0.765254 18.8997 0.500322 18.7897 0.305014 18.5942C0.109706 18.3987 -1.40128e-07 18.1337 0 17.8573V1.04265C0.00192155 0.766897 0.112245 0.502968 0.307139 0.307879C0.502032 0.112789 0.765849 0.00219938 1.0416 0H19.9584ZM6.3 4.2C6.85695 4.2 7.3911 4.42125 7.78492 4.81508C8.17875 5.2089 8.4 5.74305 8.4 6.3C8.4 6.85695 8.17875 7.3911 7.78492 7.78492C7.3911 8.17875 6.85695 8.4 6.3 8.4C5.74305 8.4 5.2089 8.17875 4.81508 7.78492C4.42125 7.3911 4.2 6.85695 4.2 6.3C4.2 5.74305 4.42125 5.2089 4.81508 4.81508C5.2089 4.42125 5.74305 4.2 6.3 4.2V4.2Z"
        fill="var(--theme-secondary)"
      />
    </svg>
  );
  if (form.productType === 3) return null;

  return (
    <section className="product-form-card flex flex-column gap-4">
      <FormSectionHeader
        icon={svgIcon}
        title={localeText(isService ? "addServiceImages" : "addProductImages")}
        subtitle={localeText(isService ? "addServiceImagesSubtitle" : "addProductImagesSubtitle")}
      />
      <FileDropzone
        accept="image/jpeg,image/png,image/webp"
        multiple
        onSelect={(files) => form.addImages(files)}
        formatsKey="uploadZoneFormatsProductImages"
        buttonKey="uploadFiles"
        maxBytes={IMAGE_MAX_BYTES}
      />
      {form.images?.length > 0 && (
        <ul className="product-form-file-list">
          {form.images.slice(0, MAX_IMAGES).map((file, index) => (
            <FileListItem
              key={file.id || file.previewUrl || index}
              file={file}
              isPrimary={index === 0}
              isImage
              onRemove={() => form.removeImage(index)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

export default ImagesSection;
